import { useCallback, useEffect, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PrimaryButton } from '@pulse-web-ui/button-v2';

import { sendAnalyticEvent } from '@app/web-analytic';

import { useSubmitProfileEdit } from '@entities/index';

import {
  AnalyticEvent,
  SUBMIT_EDIT_PROFILE_ERROR,
  SUBMIT_EDIT_PROFILE_SUCCESS,
  addTestAttribute,
  getMismatches,
  sendRequestErrorsAnalytics,
} from '@shared/index';

import type { SubmitProfileEditValues } from '../../../../model';

export const SubmitProfileEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    handleSubmit,
    formState: { errors },
  } = useFormContext<SubmitProfileEditValues>();

  const [requestBody, setRequestBody] = useState<FormData>();

  const {
    res,
    refetch: submitProfileEdit,
    error,
    isFetching,
    resetQuerryError,
  } = useSubmitProfileEdit(requestBody);

  useEffect(() => {
    if (error) {
      sendRequestErrorsAnalytics(
        AnalyticEvent.AGENT_EDITPERSONALDATA_ERROR,
        getMismatches(error)
      );

      resetQuerryError();
      navigate(SUBMIT_EDIT_PROFILE_ERROR);
    }
  }, [error]);

  useEffect(() => {
    if (!!requestBody) {
      sendAnalyticEvent(AnalyticEvent.AGENT_EDITPERSONALDATA_SEND);
      submitProfileEdit();
    }
  }, [requestBody]);

  useEffect(() => {
    if (!isFetching && res) {
      sendAnalyticEvent(AnalyticEvent.AGENT_EDITPERSONALDATA_SUCCESS);
      navigate(SUBMIT_EDIT_PROFILE_SUCCESS);
    }
  }, [isFetching, res]);

  const handleSendRequestClick = useCallback(
    handleSubmit((values) => {
      const { files } = values;

      if (!files?.length) {
        return submitProfileEdit();
      }

      const formData = new FormData();

      files.forEach((file) => {
        formData.append(
          'file',
          new Blob([file], { type: file.type }),
          file.name
        );
      });
      setRequestBody(formData);
    }),
    [submitProfileEdit]
  );

  return (
    <PrimaryButton
      size='l'
      onClick={handleSendRequestClick}
      disabled={!!Object.keys(errors).length}
      isLoading={isFetching}
      {...addTestAttribute('profileEdit.submitButton')}
    >
      {t('COMMON:buttons.sendRequest') || ''}
    </PrimaryButton>
  );
};
